import React, { useState, useEffect } from "react";
import { isImpersonating, guardAction } from "../../utils/impersonate";
import { PnlCell } from "../../components/ui/shared";

const safeMonth = (s) => (!s || s.startsWith('0001')) ? '—' : s;
const safeDate  = (s) => (!s || s.startsWith('0001')) ? '—' : s;
const fmt = (n, d=2) => Number(n||0).toLocaleString("en-US",{minimumFractionDigits:d,maximumFractionDigits:d});
const fmtUSD = (n, d=2) => `$${fmt(n, d)}`;

const BANK = { name:"Bank Mandiri", account:"1180007827669", holder:"Hendri Saputro" };
const METHODS = [
  { id:"bank_transfer", label:"Bank Transfer", desc:"Mandiri · Indonesia", available:true },
  { id:"crypto", label:"Crypto (USDT)", desc:"USDT TRC20 · Instant", available:true, minCheck:true },
  { id:"paypal", label:"PayPal", desc:"Coming soon", available:false },
  { id:"stripe", label:"Credit Card", desc:"Coming soon", available:false },
];

const Badge = ({ s }) => {
  const map = {
    pending:   { bg:"#f59e0b22", color:"var(--warning)", label:"Pending" },
    verifying: { bg:"#3b82f622", color:"var(--accent)", label:"Under Review" },
    approved:  { bg:"#10b98122", color:"var(--success)", label:"Approved" },
    rejected:  { bg:"#ef444422", color:"var(--danger)", label:"Rejected" },
    cancelled: { bg:"#6b728022", color:"var(--text-muted)", label:"Cancelled" },
    paid:      { bg:"#10b98122", color:"var(--success)", label:"Paid" },
    void:      { bg:"#ef444422", color:"var(--danger)", label:"Void" },
    active:    { bg:"#a78bfa22", color:"var(--purple,#a78bfa)", label:"Active" },
    expired:   { bg:"#ef444422", color:"var(--danger)", label:"Expired" },
    trial:     { bg:"#f59e0b22", color:"var(--warning)", label:"Trial" },
    admin:     { bg:"#6b728022", color:"var(--text-muted)", label:"Admin" },
  };
  const st = map[s] || { bg:"#6b728022", color:"var(--text-muted)", label:s };
  return <span style={{background:st.bg,color:st.color,padding:"2px 10px",borderRadius:20,
    fontSize:11,fontWeight:700,border:`1px solid ${st.color}44`}}>{st.label}</span>;
};

const S = {
  page: { padding: "24px 0", maxWidth: 1200, margin: "0 auto", display: "flex", flexDirection: "column", gap: 14 },
  card:  { background:"var(--bg-surface)", border:"1px solid var(--border)", borderRadius:12, padding:"20px", marginBottom:16 },
  h2:    { fontSize:15, fontWeight:700, color:"var(--text-main)", marginBottom:12 },
  muted: { fontSize:12, color:"var(--text-muted)" },
  btn:   { padding:"10px 20px", borderRadius:8, border:"none", cursor:"pointer", fontSize:13, fontWeight:700 },
  th:    { padding:"8px 10px", textAlign:"left", fontSize:11, fontWeight:700, color:"var(--text-muted)",
           textTransform:"uppercase", letterSpacing:"0.04em", whiteSpace:"nowrap" },
  td:    { padding:"10px 10px", fontSize:12 },
};

// ── Tab: Subscription ────────────────────────────────────────────────────────
function TabSubscription({ tier, tiers, requests, onReload }) {
  const [step, setStep]       = useState("plan");
  const [selected, setSelected] = useState({ tier:"premium", cycle:"monthly", method:"crypto" });
  const [proof, setProof]     = useState(null);
  const [note, setNote]       = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast]     = useState(null);

  const authH = () => ({ Authorization:`Bearer ${localStorage.getItem("access_token")||localStorage.getItem("token")||""}` });
  const showMsg = (msg, ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast(null),5000); };

  const cancelRequest = async (id) => {
    if (!confirm("Cancel this payment request?")) return;
    const r = await fetch(`/api/investor/payment-request/${id}/cancel`,
      { method:"POST", headers:authH() }).then(r=>r.json());
    if (r.ok) { showMsg("Request cancelled"); onReload(); }
    else showMsg(r.error||"Failed", false);
  };

  const getPrice = () => {
    const t = tiers.find(t=>t.tier===selected.tier);
    if (!t) return 0;
    return selected.cycle==="annual" ? t.priceUsd*12*0.8 : t.priceUsd;
  };

  const submit = async () => {
    setSubmitting(true);
    if (selected.method==="bank_transfer" && !proof) {
      showMsg("Please upload your transfer proof screenshot", false);
      setSubmitting(false); return;
    }
    try {
      if (selected.method==="crypto") {
        const r = await fetch("/api/investor/payment-request/crypto", {
          method:"POST", headers:{...authH(),"Content-Type":"application/json"},
          body:JSON.stringify({ tier:selected.tier, cycle:selected.cycle })
        }).then(r=>r.json());
        setSubmitting(false);
        if (r.ok && r.payUrl) window.location.href = r.payUrl;
        else showMsg(r.error||"Failed to create crypto payment", false);
      } else {
        const fd = new FormData();
        fd.append("tier", selected.tier); fd.append("cycle", selected.cycle);
        fd.append("method", selected.method); fd.append("note", note);
        if (proof) fd.append("proof", proof);
        const r = await fetch("/api/investor/payment-request",
          { method:"POST", headers:authH(), body:fd }).then(r=>r.json());
        setSubmitting(false);
        if (r.ok) { setStep("done"); onReload(); }
        else showMsg(r.error||"Failed to submit", false);
      }
    } catch(e) { setSubmitting(false); showMsg("Network error", false); }
  };

  const hasPending     = requests.some(r=>r.status==="pending"||r.status==="verifying");
  const cryptoAvailable = !(selected.tier==="basic" && selected.cycle==="monthly");
  const isPremium      = tier?.tier==="premium" && tier?.status!=="expired";

  // Format tanggal next billing
  const nextBilling = tier?.nextBillingDate
    ? new Date(tier.nextBillingDate).toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"})
    : null;
  const subStart = tier?.subscriptionStartedAt
    ? new Date(tier.subscriptionStartedAt).toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"})
    : null;

  return (
    <div>
      {toast && <div style={{position:"fixed",bottom:24,right:24,zIndex:2000,
        background:toast.ok?"var(--success)":"var(--danger)",color:"#fff",
        padding:"12px 20px",borderRadius:8,fontWeight:600,fontSize:13}}>{toast.msg}</div>}

      {/* Current Plan Card */}
      <div style={S.card}>
        <div style={S.h2}>Current Subscription</div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:16}}>
          <div style={{flex:1}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
              <span style={{fontSize:22,fontWeight:800,color:"var(--text-main)",textTransform:"capitalize"}}>
                {tier?.tier||"None"}
              </span>
              <Badge s={tier?.status||"none"}/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:10,marginTop:10}}>
              {subStart && (
                <div style={{background:"var(--bg-elevated)",borderRadius:8,padding:"10px 14px"}}>
                  <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:2}}>Started</div>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>{subStart}</div>
                </div>
              )}
              {nextBilling && (
                <div style={{background:"var(--bg-elevated)",borderRadius:8,padding:"10px 14px"}}>
                  <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:2}}>
                    {tier?.status==="expired" ? "Expired On" : "Renews On"}
                  </div>
                  <div style={{fontSize:13,fontWeight:600,
                    color:tier?.status==="expired"?"var(--danger)":"var(--accent)"}}>
                    {nextBilling}
                  </div>
                </div>
              )}
              {tier?.trialEndsAt && (
                <div style={{background:"#f59e0b11",border:"1px solid #f59e0b33",borderRadius:8,padding:"10px 14px"}}>
                  <div style={{fontSize:10,color:"var(--warning)",marginBottom:2}}>Trial Ends</div>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--warning)"}}>{tier.trialEndsAt}</div>
                </div>
              )}
              <div style={{background:"var(--bg-elevated)",borderRadius:8,padding:"10px 14px"}}>
                <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:2}}>Features</div>
                <div style={{fontSize:12,color:"var(--text-main)"}}>
                  {tier?.tier==="premium"
                    ? "Full access — all features unlocked"
                    : "1 follower account"}
                </div>
              </div>
            </div>
          </div>
          <div>
            {!isPremium && !hasPending && (
              <button style={{...S.btn,background:"linear-gradient(135deg,#7c3aed,#a78bfa)",color:"#fff"}}
                onClick={()=>setStep("plan")}>Upgrade to Premium</button>
            )}
            {hasPending && <Badge s="verifying"/>}
            {isPremium && <Badge s="active"/>}
          </div>
        </div>
      </div>

      {/* Upgrade Flow */}
      {!isPremium && (
        <div style={S.card}>
          <div style={S.h2}>{step==="done"?"Request Submitted":"Upgrade Plan"}</div>
          {step==="done" ? (
            <div style={{textAlign:"center",padding:"24px 0"}}>
              <div style={{fontSize:40,marginBottom:12}}></div>
              <div style={{fontSize:16,fontWeight:700,color:"var(--success)",marginBottom:8}}>Payment request submitted!</div>
              <div style={{fontSize:13,color:"var(--text-muted)",marginBottom:20}}>
                Admin will review within 1-2 business days.
              </div>
              <button style={{...S.btn,background:"var(--bg-elevated)",color:"var(--text-muted)",border:"1px solid var(--border)"}}
                onClick={()=>setStep("plan")}>Back</button>
            </div>
          ) : step==="plan" ? (
            <div>
              <div style={{fontSize:12,fontWeight:700,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>Select Plan</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
                {tiers.filter(t=>t.tier!=="trial").map(t=>(
                  <div key={t.tier} onClick={()=>setSelected(s=>({...s,tier:t.tier}))}
                    style={{borderRadius:8,padding:"14px",cursor:"pointer",
                      border:selected.tier===t.tier
                        ?(t.tier==="premium"?"2px solid #a78bfa":"2px solid var(--accent)")
                        :"1px solid var(--border)",
                      background:selected.tier===t.tier
                        ?(t.tier==="premium"?"rgba(167,139,250,0.08)":"var(--accent-soft)")
                        :"var(--bg-elevated)"}}>
                    <div style={{fontSize:14,fontWeight:700,color:t.tier==="premium"?"var(--purple,#a78bfa)":"var(--text-main)",marginBottom:2}}>{t.label}</div>
                    <div style={{fontSize:18,fontWeight:800,color:"var(--text-main)"}}>
                      ${fmt(t.priceUsd)}<span style={{fontSize:11,fontWeight:400,color:"var(--text-muted)"}}>/mo</span>
                    </div>
                    <div style={{fontSize:11,color:"var(--text-muted)",marginTop:4}}>{t.description}</div>
                  </div>
                ))}
              </div>
              <div style={{fontSize:12,fontWeight:700,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>Billing Cycle</div>
              <div style={{display:"flex",gap:10,marginBottom:20}}>
                {[["monthly","Monthly"],["annual","Annual (20% off)"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setSelected(s=>({...s,cycle:v}))}
                    style={{flex:1,padding:"10px",borderRadius:8,cursor:"pointer",fontSize:12,fontWeight:600,
                      border:selected.cycle===v?"2px solid var(--accent)":"1px solid var(--border)",
                      background:selected.cycle===v?"var(--accent-soft)":"var(--bg-elevated)",
                      color:selected.cycle===v?"var(--accent)":"var(--text-muted)"}}>
                    {l}
                  </button>
                ))}
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                padding:"14px 16px",background:"var(--bg-elevated)",borderRadius:8,marginBottom:16}}>
                <div style={{fontSize:13,color:"var(--text-muted)"}}>Total due</div>
                <div style={{fontSize:20,fontWeight:800,color:"var(--text-main)"}}>
                  ${fmt(getPrice())}
                  <span style={{fontSize:12,fontWeight:400,color:"var(--text-muted)",marginLeft:4}}>
                    {selected.cycle==="annual"?"/ year":"/ month"}
                  </span>
                </div>
              </div>
              <button style={{...S.btn,width:"100%",background:"linear-gradient(135deg,#7c3aed,#a78bfa)",color:"#fff"}}
                onClick={()=>setStep("method")}>Continue</button>
            </div>
          ) : step==="method" ? (
            <div>
              <div style={{fontSize:12,fontWeight:700,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>Payment Method</div>
              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:20}}>
                {METHODS.map(m=>(
                  <div key={m.id}
                    onClick={()=>(m.available&&(!m.minCheck||cryptoAvailable))&&setSelected(s=>({...s,method:m.id}))}
                    style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                      padding:"14px 16px",borderRadius:8,
                      cursor:(m.available&&(!m.minCheck||cryptoAvailable))?"pointer":"not-allowed",
                      opacity:(m.available&&(!m.minCheck||cryptoAvailable))?1:0.5,
                      border:selected.method===m.id?"2px solid var(--accent)":"1px solid var(--border)",
                      background:selected.method===m.id?"var(--accent-soft)":"var(--bg-elevated)"}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>{m.label}</div>
                      <div style={{fontSize:11,color:"var(--text-muted)"}}>{m.desc}</div>
                    </div>
                    {!m.available && <Badge s="soon"/>}
                    {m.available && selected.method===m.id && <span style={{color:"var(--accent)",fontSize:16}}></span>}
                  </div>
                ))}
              </div>
              {selected.method==="bank_transfer" && (
                <div style={{padding:"16px",background:"rgba(78,154,241,0.06)",border:"1px solid rgba(78,154,241,0.2)",borderRadius:8,marginBottom:16}}>
                  <div style={{fontSize:12,fontWeight:700,color:"var(--accent)",marginBottom:10}}>Transfer Details</div>
                  {[["Bank",BANK.name],["Account Number",BANK.account],["Account Name",BANK.holder],["Amount",`$${fmt(getPrice())} USD`]].map(([k,v])=>(
                    <div key={k} style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                      <span style={{fontSize:12,color:"var(--text-muted)"}}>{k}</span>
                      <span style={{fontSize:12,fontWeight:700,color:"var(--text-main)",userSelect:"all"}}>{v}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{display:"flex",gap:10}}>
                <button style={{...S.btn,flex:1,background:"var(--bg-elevated)",color:"var(--text-muted)",border:"1px solid var(--border)"}}
                  onClick={()=>setStep("plan")}>Back</button>
                <button style={{...S.btn,flex:2,background:"var(--accent)",color:"#fff"}}
                  onClick={()=>selected.method==="crypto"?submit():setStep("proof")}>
                  {selected.method==="crypto"?(submitting?"Processing...":"Pay with USDT TRC20"):"I Have Transferred"}
                </button>
              </div>
            </div>
          ) : step==="proof" ? (
            <div>
              <div style={{marginBottom:14}}>
                <label style={{fontSize:12,fontWeight:700,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:"0.06em",display:"block",marginBottom:6}}>Transfer Proof (screenshot)</label>
                <input type="file" accept="image/*,.pdf" onChange={e=>setProof(e.target.files[0])}
                  style={{width:"100%",padding:"10px",background:"var(--bg-elevated)",border:"1px solid var(--border)",borderRadius:8,color:"var(--text-muted)",fontSize:12}}/>
                {proof && <div style={{fontSize:11,color:"var(--success)",marginTop:4}}>Selected: {proof.name}</div>}
              </div>
              <div style={{marginBottom:16}}>
                <label style={{fontSize:12,fontWeight:700,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:"0.06em",display:"block",marginBottom:6}}>Note (optional)</label>
                <textarea value={note} onChange={e=>setNote(e.target.value)} rows={3}
                  placeholder="e.g. Transfer reference number, date..."
                  style={{width:"100%",padding:"10px 14px",background:"var(--bg-elevated)",border:"1px solid var(--border)",borderRadius:8,color:"var(--text-muted)",fontSize:13,outline:"none",resize:"vertical",boxSizing:"border-box"}}/>
              </div>
              <div style={{padding:"12px 16px",background:"var(--bg-elevated)",borderRadius:8,marginBottom:16,fontSize:12}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <span style={{color:"var(--text-muted)"}}>Plan</span>
                  <span style={{fontWeight:700,color:"var(--text-main)",textTransform:"capitalize"}}>{selected.tier} · {selected.cycle}</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <span style={{color:"var(--text-muted)"}}>Amount</span>
                  <span style={{fontWeight:700,color:"var(--text-main)"}}>${fmt(getPrice())}</span>
                </div>
              </div>
              <div style={{display:"flex",gap:10}}>
                <button style={{...S.btn,flex:1,background:"var(--bg-elevated)",color:"var(--text-muted)",border:"1px solid var(--border)"}}
                  onClick={()=>setStep("method")}>Back</button>
                <button disabled={submitting} style={{...S.btn,flex:2,background:"linear-gradient(135deg,#7c3aed,#a78bfa)",color:"#fff",opacity:submitting?0.6:1}}
                  onClick={submit}>{submitting?"Submitting...":"Submit Payment Request"}</button>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Payment History */}
      {requests.length > 0 && (
        <div style={S.card}>
          <div style={S.h2}>Subscription Payment History</div>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead>
                <tr style={{borderBottom:"1px solid var(--border)"}}>
                  {["Date","Plan","Cycle","Amount","Method","Status","Note",""].map(h=>(
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {requests.map(r=>(
                  <tr key={r.id} style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                    <td style={{...S.td,color:"var(--text-muted)"}}>{r.createdAt}</td>
                    <td style={{...S.td,fontWeight:600,color:"var(--text-main)",textTransform:"capitalize"}}>{r.tier}</td>
                    <td style={{...S.td,color:"var(--text-muted)",textTransform:"capitalize"}}>{r.cycle}</td>
                    <td style={{...S.td,fontWeight:600,color:"var(--text-main)"}}>${fmt(r.amountUsd)}</td>
                    <td style={{...S.td,color:"var(--text-muted)",textTransform:"capitalize"}}>{r.method.replace("_"," ")}</td>
                    <td style={{...S.td}}><Badge s={r.status==="verifying"?"verifying":r.status}/></td>
                    <td style={{...S.td,color:"var(--text-muted)",maxWidth:180,fontSize:11}}>{r.adminNote||r.note||"—"}</td>
                    <td style={{...S.td}}>
                      {r.status==="pending" && (
                        <button onClick={()=>cancelRequest(r.id)}
                          style={{padding:"4px 10px",borderRadius:6,border:"1px solid var(--danger)",
                            background:"transparent",color:"var(--danger)",fontSize:11,cursor:"pointer",fontWeight:600}}>
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Pay Modal ────────────────────────────────────────────────────────────────
function PayModal({ inv, onClose, onSuccess }) {
  const [method, setMethod] = useState("bank_transfer");
  const [ref,    setRef]    = useState("");
  const [notes,  setNotes]  = useState("");
  const [loading,setLoading]= useState(false);
  const [err,    setErr]    = useState("");

  const BANK = { name:"Bank Mandiri", account:"1180007827669", holder:"Hendri Saputro" };
  const authH = () => ({ Authorization:`Bearer ${localStorage.getItem("access_token")||localStorage.getItem("token")||""}` });

  const submit = async () => {
    if (!ref.trim()) { setErr("Payment reference / TX hash required"); return; }
    setLoading(true); setErr("");
    try {
      const r = await fetch(`/api/investor/invoices/${inv.id}/submit-payment`, {
        method:"POST",
        headers:{...authH(),"Content-Type":"application/json"},
        body: JSON.stringify({ payment_method:method, payment_ref:ref.trim(), notes })
      }).then(r=>r.json());
      if (r.ok) { onSuccess(); onClose(); }
      else setErr(r.error||"Failed to submit");
    } catch(e) { setErr("Network error"); }
    finally { setLoading(false); }
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:3000,
      display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"var(--bg-surface)",borderRadius:14,padding:24,
        width:"100%",maxWidth:460,border:"1px solid var(--border)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={{fontSize:15,fontWeight:700,color:"var(--text-main)"}}>Submit Payment</div>
          <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",
            color:"var(--text-muted)",fontSize:18}}></button>
        </div>

        {/* Invoice summary */}
        <div style={{background:"var(--bg-elevated)",borderRadius:8,padding:"12px 14px",marginBottom:16}}>
          {[
            ["Invoice",  inv.invoice_number],
            ["Trader",   inv.trader_name],
            ["Month",    safeMonth(inv.billing_month)],
            ["Amount Due", fmtUSD(inv.total_amount)],
          ].map(([k,v])=>(
            <div key={k} style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
              <span style={{fontSize:12,color:"var(--text-muted)"}}>{k}</span>
              <span style={{fontSize:12,fontWeight:700,color:k==="Amount Due"?"var(--warning)":"var(--text-main)"}}>{v}</span>
            </div>
          ))}
        </div>

        {/* Method */}
        <div style={{marginBottom:12}}>
          <div style={{fontSize:11,fontWeight:700,color:"var(--text-muted)",textTransform:"uppercase",
            letterSpacing:"0.06em",marginBottom:6}}>Payment Method</div>
          <div style={{display:"flex",gap:8}}>
            {[["bank_transfer","Bank Transfer"],["crypto","USDT TRC20"]].map(([v,l])=>(
              <button key={v} onClick={()=>setMethod(v)}
                style={{flex:1,padding:"8px",borderRadius:8,cursor:"pointer",fontSize:12,fontWeight:600,
                  border:method===v?"2px solid var(--accent)":"1px solid var(--border)",
                  background:method===v?"var(--accent-soft)":"var(--bg-elevated)",
                  color:method===v?"var(--accent)":"var(--text-muted)"}}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Bank details */}
        {method==="bank_transfer" && (
          <div style={{padding:"12px",background:"rgba(78,154,241,0.06)",
            border:"1px solid rgba(78,154,241,0.2)",borderRadius:8,marginBottom:12,fontSize:12}}>
            {[["Bank",BANK.name],["Account",BANK.account],["Name",BANK.holder],
              ["Amount",fmtUSD(inv.total_amount)+" USD"]].map(([k,v])=>(
              <div key={k} style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                <span style={{color:"var(--text-muted)"}}>{k}</span>
                <span style={{fontWeight:700,color:"var(--text-main)",userSelect:"all"}}>{v}</span>
              </div>
            ))}
          </div>
        )}

        {/* Reference */}
        <div style={{marginBottom:12}}>
          <label style={{fontSize:11,fontWeight:700,color:"var(--text-muted)",textTransform:"uppercase",
            letterSpacing:"0.06em",display:"block",marginBottom:4}}>
            {method==="crypto"?"USDT TX Hash":"Transfer Reference Number"} *
          </label>
          <input value={ref} onChange={e=>setRef(e.target.value)}
            placeholder={method==="crypto"?"0x...abc123":"e.g. 2026061100123456"}
            style={{width:"100%",padding:"9px 12px",background:"var(--bg-elevated)",
              border:"1px solid var(--border)",borderRadius:8,color:"var(--text-main)",
              fontSize:13,outline:"none",boxSizing:"border-box"}}/>
        </div>

        {/* Notes */}
        <div style={{marginBottom:16}}>
          <label style={{fontSize:11,fontWeight:700,color:"var(--text-muted)",textTransform:"uppercase",
            letterSpacing:"0.06em",display:"block",marginBottom:4}}>Note (optional)</label>
          <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={2}
            placeholder="Additional info for admin..."
            style={{width:"100%",padding:"9px 12px",background:"var(--bg-elevated)",
              border:"1px solid var(--border)",borderRadius:8,color:"var(--text-muted)",
              fontSize:13,outline:"none",resize:"none",boxSizing:"border-box"}}/>
        </div>

        {err && <div style={{color:"var(--danger)",fontSize:12,marginBottom:10}}>{err}</div>}

        <div style={{display:"flex",gap:8}}>
          <button onClick={onClose}
            style={{flex:1,padding:"10px",borderRadius:8,border:"1px solid var(--border)",
              background:"var(--bg-elevated)",color:"var(--text-muted)",fontSize:13,
              fontWeight:600,cursor:"pointer"}}>Cancel</button>
          <button onClick={submit} disabled={loading}
            style={{flex:2,padding:"10px",borderRadius:8,border:"none",
              background:"var(--accent)",color:"#fff",fontSize:13,
              fontWeight:700,cursor:"pointer",opacity:loading?0.6:1}}>
            {loading?"Submitting...":"Submit Payment"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tab: Performance Fees ────────────────────────────────────────────────────
function TabPerformanceFees() {
  const [invoices,  setInvoices]  = useState([]);
  const [feeStatus, setFeeStatus] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [tab,       setTab]       = useState("invoices");
  const [payModal,  setPayModal]  = useState(null); // invoice object

  const authH = () => ({ Authorization:`Bearer ${localStorage.getItem("access_token")||localStorage.getItem("token")||""}` });

  useEffect(() => {
    Promise.all([
      fetch("/api/investor/invoices",   { headers:authH() }).then(r=>r.json()),
      fetch("/api/investor/fee-status", { headers:authH() }).then(r=>r.json()),
    ]).then(([inv, fs]) => {
      if (inv.ok) setInvoices(inv.invoices || []);
      if (fs.ok)  setFeeStatus(fs.fee_status || []);
    }).finally(() => setLoading(false));
  }, []);

  const totalPending = invoices.filter(i=>i.status==="pending").reduce((s,i)=>s+(i.total_amount||0),0);
  const totalPaid    = invoices.filter(i=>i.status==="paid").reduce((s,i)=>s+(i.total_amount||0),0);
  const totalAccrued = feeStatus.reduce((s,f)=>s+(f.accrued_fee||0),0);

  if (loading) return <div style={{padding:40,textAlign:"center",color:"var(--text-muted)"}}>Loading...</div>;

  return (
    <div>
      {/* Summary */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12,marginBottom:20}}>
        {[
          { label:"Accruing (This Month)", value:fmtUSD(totalAccrued), color:"var(--accent)",  sub:"Estimated, not billed yet" },
          { label:"Pending Invoices",      value:fmtUSD(totalPending), color:"var(--warning)",        sub:"Due for payment" },
          { label:"Total Paid",            value:fmtUSD(totalPaid),    color:"var(--success)", sub:"Already settled" },
        ].map((c,i)=>(
          <div key={i} style={{...S.card,padding:"14px 16px",marginBottom:0}}>
            <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:4}}>{c.label}</div>
            <div style={{fontSize:18,fontWeight:700,color:c.color}}>{c.value}</div>
            <div style={{fontSize:10,color:"var(--text-muted)",marginTop:2}}>{c.sub}</div>
          </div>
        ))}
      </div>

      {/* How it works */}
      <div style={{...S.card,background:"rgba(99,102,241,0.05)",border:"1px solid rgba(99,102,241,0.2)"}}>
        <div style={{display:"flex",gap:24,flexWrap:"wrap"}}>
          {[
            { icon:"", title:"25% of Profit",        desc:"Performance fee charged on copy trade profits above High Water Mark" },
            { icon:"", title:"High Water Mark",       desc:"You only pay fees on NEW profits — never on recovering previous losses" },
            { icon:"", title:"Monthly Settlement",    desc:"Billed on the 1st of each month for the prior month's performance" },
            { icon:"  ", title:"Transparency",          desc:"Full breakdown per trader per month — see exact P&L, HWM, and fee calculation" },
          ].map((item,i)=>(
            <div key={i} style={{display:"flex",gap:8,flex:"1 1 180px",alignItems:"flex-start"}}>
              <span style={{fontSize:18}}>{item.icon}</span>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:"var(--text-main)",marginBottom:2}}>{item.title}</div>
                <div style={{fontSize:11,color:"var(--text-muted)",lineHeight:1.5}}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Subtabs */}
      <div style={{display:"flex",gap:4,marginBottom:16,borderBottom:"1px solid var(--border)"}}>
        {[["invoices","Invoice History"],["accruing","Accruing Now"]].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)}
            style={{padding:"8px 18px",border:"none",cursor:"pointer",fontSize:13,fontWeight:600,
              background:"transparent",marginBottom:-1,
              borderBottom:tab===id?"2px solid var(--accent)":"2px solid transparent",
              color:tab===id?"var(--accent)":"var(--text-muted)"}}>
            {label}
          </button>
        ))}
      </div>

      {/* Invoices */}
      {tab==="invoices" && (
        <div style={S.card}>
          {invoices.length===0 ? (
            <div style={S.muted}>No invoices yet. Billing runs on the 1st of each month.</div>
          ) : (
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead>
                  <tr style={{borderBottom:"1px solid var(--border)"}}>
                    {["Invoice #","Trader","Month","Copy P&L","Above HWM","Fee Rate","Your Cost","Due Date","Status"].map(h=>(
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv,i) => {
                    const d = inv.detail || {};

                    // Fee Engine V2 (volume/management/platform/joining, invoice gabungan) —
                    // struktur detail beda total dari performance-fee (breakdown array,
                    // TIDAK ada performance_rate/cumulative_pnl/HWM), render baris terpisah
                    // supaya tidak salah tampil "25%" atau kolom kosong menyesatkan.
                    if (inv.invoice_type === "fee_v2") {
                      const breakdown = d.breakdown || [];
                      return (
                        <tr key={i} style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                          <td style={{...S.td,color:"var(--accent)",fontWeight:600}}>{inv.invoice_number}</td>
                          <td style={{...S.td,fontWeight:600,color:"var(--text-main)"}} colSpan={2}>
                            Platform Fees
                            {breakdown.length>0 && (
                              <div style={{fontSize:10,color:"var(--text-muted)",fontWeight:400,marginTop:2}}>
                                {breakdown.map(b=>`${b.fee_type} $${Number(b.amount_usd).toFixed(2)}`).join(" · ")}
                              </div>
                            )}
                          </td>
                          <td style={S.td} colSpan={2}/>
                          <td style={{...S.td,color:"var(--text-muted)"}}>—</td>
                          <td style={{...S.td,color:"var(--warning)",fontWeight:700}}>{fmtUSD(inv.total_amount)}</td>
                          <td style={{...S.td,color:"var(--text-muted)",whiteSpace:"nowrap"}}>{safeDate(inv.due_date)}</td>
                          <td style={{...S.td}}>
                            <div style={{display:"flex",gap:6,alignItems:"center"}}>
                              <Badge s={inv.status}/>
                              {(inv.status==="pending"||inv.status==="overdue") && (
                                <button onClick={()=>setPayModal(inv)}
                                  style={{padding:"3px 10px",borderRadius:6,border:"1px solid var(--accent)",
                                    background:"transparent",color:"var(--accent)",fontSize:11,
                                    cursor:"pointer",fontWeight:700,whiteSpace:"nowrap"}}>
                                  Pay
                                </button>
                              )}
                              {inv.status==="under_review" && (
                                <span style={{fontSize:10,color:"var(--accent)",fontStyle:"italic"}}>Under Review</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    const cumPnl   = inv.live_cumulative_pnl   != null ? inv.live_cumulative_pnl   : d.cumulative_pnl;
                    const aboveHwm = inv.live_profit_above_hwm != null ? inv.live_profit_above_hwm : d.profit_above_hwm;
                    const yourCost = inv.live_total_fee != null ? inv.live_total_fee : inv.total_amount;
                    return (
                      <tr key={i} style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                        <td style={{...S.td,color:"var(--accent)",fontWeight:600}}>{inv.invoice_number}</td>
                        <td style={{...S.td,fontWeight:600,color:"var(--text-main)"}}>{inv.trader_name}</td>
                        <td style={{...S.td,color:"var(--text-muted)"}}>{safeMonth(inv.billing_month)}</td>
                        <td style={S.td}><PnlCell value={cumPnl}/></td>
                        <td style={{...S.td,color:"var(--accent)",fontWeight:600}}>{fmtUSD(aboveHwm)}</td>
                        <td style={{...S.td,color:"var(--text-muted)"}}>
                          {d.performance_rate ? `${(d.performance_rate*100).toFixed(0)}%` : "25%"}
                        </td>
                        <td style={{...S.td,color:"var(--warning)",fontWeight:700}}>{fmtUSD(yourCost)}</td>
                        <td style={{...S.td,color:"var(--text-muted)",whiteSpace:"nowrap"}}>{safeDate(inv.due_date)}</td>
                        <td style={{...S.td}}>
                          <div style={{display:"flex",gap:6,alignItems:"center"}}>
                            <Badge s={inv.status}/>
                            {(inv.status==="pending"||inv.status==="overdue") && (
                              <button onClick={()=>setPayModal(inv)}
                                style={{padding:"3px 10px",borderRadius:6,border:"1px solid var(--accent)",
                                  background:"transparent",color:"var(--accent)",fontSize:11,
                                  cursor:"pointer",fontWeight:700,whiteSpace:"nowrap"}}>
                                Pay
                              </button>
                            )}
                            {inv.status==="under_review" && (
                              <span style={{fontSize:10,color:"var(--accent)",fontStyle:"italic"}}>Under Review</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{borderTop:"2px solid var(--border)"}}>
                    <td colSpan={6} style={{...S.td,fontWeight:700,color:"var(--text-muted)"}}>TOTAL DUE (Pending)</td>
                    <td style={{...S.td,fontSize:14,fontWeight:800,color:"var(--warning)"}}>{fmtUSD(totalPending)}</td>
                    <td colSpan={2}/>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Accruing */}
      {tab==="accruing" && (
        <div style={S.card}>
          <div style={{...S.muted,marginBottom:16}}>
            Estimated fee for the current month — not yet billed. Calculated from copy P&L above HWM.
          </div>
          {feeStatus.length===0 ? (
            <div style={S.muted}>No active copy trading periods.</div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {feeStatus.map((f,i)=>(
                <div key={i} style={{background:"var(--bg-elevated)",borderRadius:10,padding:"16px",border:"1px solid var(--border)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                    <div>
                      <div style={{fontSize:14,fontWeight:700,color:"var(--text-main)"}}>{f.trader_name}</div>
                      <div style={{fontSize:11,color:"var(--text-muted)"}}>
                        Follower: {f.follower_account} · Since {f.period_start}
                      </div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:18,fontWeight:800,color:"var(--warning)"}}>{fmtUSD(f.accrued_fee)}</div>
                      <div style={{fontSize:10,color:"var(--text-muted)"}}>estimated fee this month</div>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                    {[
                      { label:"Cumulative P&L", value:fmtUSD(f.cumulative_pnl), color:f.cumulative_pnl>=0?"var(--success)":"var(--danger)" },
                      { label:"High Water Mark", value:fmtUSD(f.hwm_pnl),       color:"var(--text-muted)" },
                      { label:"Above HWM",       value:fmtUSD(f.unrealized_above_hwm), color:"var(--accent)" },
                    ].map((item,j)=>(
                      <div key={j} style={{background:"var(--bg-surface)",borderRadius:8,padding:"8px 12px"}}>
                        <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:2}}>{item.label}</div>
                        <div style={{fontSize:13,fontWeight:700,color:item.color}}>{item.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    {payModal && <PayModal inv={payModal} onClose={()=>setPayModal(null)}
      onSuccess={()=>{ setPayModal(null); setInvoices([]); setLoading(true);
        Promise.all([
          fetch("/api/investor/invoices",   { headers:{Authorization:`Bearer ${localStorage.getItem("access_token")||localStorage.getItem("token")||""}`} }).then(r=>r.json()),
          fetch("/api/investor/fee-status", { headers:{Authorization:`Bearer ${localStorage.getItem("access_token")||localStorage.getItem("token")||""}`} }).then(r=>r.json()),
        ]).then(([inv,fs])=>{ if(inv.ok) setInvoices(inv.invoices||[]); if(fs.ok) setFeeStatus(fs.fee_status||[]); }).finally(()=>setLoading(false));
      }}/>}
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────
export default function InvestorBilling() {
  const [tab,      setTab]      = useState("subscription");
  const [tier,     setTier]     = useState(null);
  const [tiers,    setTiers]    = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading,  setLoading]  = useState(true);

  const authH = () => ({ Authorization:`Bearer ${localStorage.getItem("access_token")||localStorage.getItem("token")||""}` });

  const load = () => {
    Promise.all([
      fetch("/api/investor/tier",            { headers:authH() }).then(r=>r.json()),
      fetch("/api/public/tier-config").then(r=>r.json()),
      fetch("/api/investor/payment-requests",{ headers:authH() }).then(r=>r.json()),
    ]).then(([td, tc, pr]) => {
      if (td.ok) setTier(td);
      if (tc.ok) setTiers(tc.tiers||[]);
      if (pr.ok) setRequests(pr.requests||[]);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div style={{padding:48,textAlign:"center",color:"var(--text-muted)"}}>Loading...</div>;

  const TABS = [
    { id:"subscription",     label:"Subscription" },
    { id:"performance_fees", label:"Performance Fees" },
  ];

  return (
    <div style={S.page}>
      <div style={{marginBottom:20}}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-main)", margin: 0 }}>Billing</h1>
        <div style={S.muted}>Manage your subscription and performance fee invoices.</div>
      </div>

      {/* Main Tabs */}
      <div style={{display:"flex",gap:4,marginBottom:20,borderBottom:"1px solid var(--border)"}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{padding:"10px 22px",border:"none",cursor:"pointer",fontSize:14,fontWeight:600,
              background:"transparent",marginBottom:-1,
              borderBottom:tab===t.id?"2px solid var(--accent)":"2px solid transparent",
              color:tab===t.id?"var(--accent)":"var(--text-muted)"}}>
            {t.label}
          </button>
        ))}
      </div>

      {tab==="subscription"     && <TabSubscription tier={tier} tiers={tiers} requests={requests} onReload={load}/>}
      {tab==="performance_fees" && <TabPerformanceFees/>}
    </div>
  );
}

// Named export untuk route terpisah (backward compat)
export { TabPerformanceFees as InvestorPerformanceFees };
